import cn from 'classnames'
import classNames from 'classnames'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { FC, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { QBRFinalizeModal } from 'partner360/components/QBR/components/qbrDocumentDetails/components/qbrDocumentDetailsHeader/components/finalizeModal/QBRFinalizeModal'
import { useFinalizeDocument } from 'partner360/components/QBR/components/qbrDocumentDetails/components/qbrDocumentDetailsHeader/hooks/useFinalizeDocument'
import {
  selectIsQbrDocumentFinalized,
  selectQBRDocumentFormData,
  selectQBREntryId,
  selectUnpopulatedFieldsMessages,
} from 'partner360/features/QBR'

import { QBRSectionThatCanBeDisabled } from '../../../../shared/qbrSectionThatCanBeDisabled/QBRSectionThatCanBeDisabled'
import { TRANSLATION_KEYS } from '../../../../translations'
import { ICheckAndSaveFn } from '../../models/iUseSave'
import { selectIsQbrChanged } from '../steps/settingsStep/components/qbrSettingsForm'

import { QbrDocumentDetailsMessage } from './components/qbrDocumentDetailsMessage/QbrDocumentDetailsMessage'

import styles from './QbrDocumentDetailsHeader.module.scss'

interface IQbrDocumentDetailsHeader {
  isSavingProgress: boolean
  isRefreshingProgress: boolean
  onReset: () => void
  onRefresh: () => void
  onSaveButtonClick: ICheckAndSaveFn
  onSaveAsButtonClick: () => void
  showListOfAllDocuments: () => void
  finalizeWithoutSaving: () => void
  finalizeWithSaving: () => void
  isNotFullFilled: boolean
  isQbrLegacyDocumentMode?: boolean
}

export const QbrDocumentDetailsHeader: FC<IQbrDocumentDetailsHeader> = ({
  isSavingProgress,
  isRefreshingProgress,
  onReset,
  onRefresh,
  onSaveButtonClick,
  onSaveAsButtonClick,
  showListOfAllDocuments,
  finalizeWithoutSaving,
  finalizeWithSaving,
  isNotFullFilled,
  isQbrLegacyDocumentMode,
}) => {
  const isSettingsWasUpdated = useSelector(selectIsQbrChanged)
  const documentEntryId = useSelector(selectQBREntryId)
  const qbrDocumentData = useSelector(selectQBRDocumentFormData)
  const unpopulatedMessages = useSelector(selectUnpopulatedFieldsMessages)
  const isQbrFormChanged = useSelector(selectIsQbrChanged)
  const isDocumentFinalized = useSelector(selectIsQbrDocumentFinalized)

  const {
    showModal,
    onFinalizeWithoutSavingClick,
    onFinalizeWithSavingClick,
    openModal,
    closeModal,
  } = useFinalizeDocument({ finalizeWithoutSaving, finalizeWithSaving })

  const handleSaveButtonClick = useCallback(async () => {
    await onSaveButtonClick({
      shouldShowConfirmationDialog: false,
      isFinalized: false,
    })
  }, [onSaveButtonClick])

  const handleSaveAsButtonClick = useCallback(() => {
    onSaveAsButtonClick()
  }, [onSaveAsButtonClick])

  const isVisibleRefreshDocumentBtn = useMemo(() => {
    return Boolean(qbrDocumentData)
  }, [qbrDocumentData])

  const isVisibleFinalizeDocumentBtn = useMemo(() => {
    return Boolean(qbrDocumentData || (isQbrLegacyDocumentMode && documentEntryId))
  }, [documentEntryId, isQbrLegacyDocumentMode, qbrDocumentData])

  return (
    <div>
      <div
        className={cn(styles.buttonsContainer, {
          [styles.warning]:
            unpopulatedMessages.length > 0 || isQbrFormChanged || isDocumentFinalized,
        })}
      >
        <div className={styles.leftContent}>
          <Button
            className={cn(styles.button, styles.returnButton)}
            onClick={showListOfAllDocuments}
          >
            <i className="kbase kbase-arrow-left" />
            &nbsp;
            <Translation span text={TRANSLATION_KEYS.return} />
          </Button>
          <QbrDocumentDetailsMessage />
        </div>
        <RenderWhen condition={!isDocumentFinalized}>
          <QBRSectionThatCanBeDisabled>
            <div className={classNames(styles.rightContent)}>
              <RenderWhen condition={Boolean(documentEntryId)}>
                <Button
                  onClick={onReset}
                  variant="outlined"
                  className={styles.button}
                  disabled={!isSettingsWasUpdated || isSavingProgress}
                >
                  <Translation text={TRANSLATION_KEYS.cancel} />
                </Button>
              </RenderWhen>
              <Button
                color="success"
                className={styles.button}
                onClick={handleSaveButtonClick}
                disabled={!isSettingsWasUpdated || isSavingProgress}
              >
                <Translation text={TRANSLATION_KEYS.save} />
              </Button>
              <RenderWhen condition={Boolean(documentEntryId)}>
                <Button
                  color="primary"
                  className={styles.button}
                  onClick={handleSaveAsButtonClick}
                  disabled={isSavingProgress}
                >
                  <Translation text={TRANSLATION_KEYS.saveAs} />
                </Button>
              </RenderWhen>

              <RenderWhen condition={isVisibleRefreshDocumentBtn}>
                <Button
                  className={styles.button}
                  onClick={onRefresh}
                  disabled={isRefreshingProgress}
                >
                  <Translation text={TRANSLATION_KEYS.refreshDocument} />
                </Button>
              </RenderWhen>

              <RenderWhen condition={isVisibleFinalizeDocumentBtn}>
                <Button className={styles.button} onClick={openModal} disabled={isNotFullFilled}>
                  <Translation text={TRANSLATION_KEYS.finalizeDocument} />
                </Button>
              </RenderWhen>
            </div>
          </QBRSectionThatCanBeDisabled>
        </RenderWhen>
        <QBRFinalizeModal
          show={showModal}
          onFinalizeWithoutSavingClick={onFinalizeWithoutSavingClick}
          onFinalizeWithSavingClick={onFinalizeWithSavingClick}
          onCloseButtonClick={closeModal}
        />
      </div>
    </div>
  )
}
